import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { SectionCompletion } from './types';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';

interface Props {
  step: number;
  title: string;
  summary: string;
  state: SectionCompletion;
  onClick: () => void;
}

const BADGE_STYLES: Record<SectionCompletion, { bg: string; text: string; label: string }> = {
  done:     { bg: 'rgba(31,90,71,0.10)',          text: SOVEREIGN.bottle,     label: 'Done' },
  current:  { bg: 'rgba(31,90,71,0.10)',          text: SOVEREIGN.bottle,     label: 'Active' },
  warn:     { bg: 'rgba(201,169,97,0.15)',         text: SOVEREIGN.champagne,  label: 'Needs review' },
  todo:     { bg: SOVEREIGN.paper,                text: SOVEREIGN.kpiCaption, label: 'Not yet' },
  optional: { bg: SOVEREIGN.paper,                text: SOVEREIGN.kpiCaption, label: 'Optional' },
  na:       { bg: SOVEREIGN.paper,                text: SOVEREIGN.barTrack,   label: 'N/A' },
};

/**
 * Collapsed one-line summary row for a non-active section.
 *
 * Visual: dashed border, step number tile, title + summary, state badge, chevron.
 * Motion: hover lifts shadow + chevron nudges right.
 */
export default function SectionPreview({
  step,
  title,
  summary,
  state,
  onClick,
}: Props) {
  const badge = BADGE_STYLES[state];

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.995 }}
      className="group flex w-full items-center gap-3 rounded-2xl px-5 py-3.5 text-left transition-shadow hover:shadow-md"
      style={{
        border: `1px dashed ${SOVEREIGN.cardBorder}`,
        background: SOVEREIGN.cardBg,
      }}
    >
      <span
        className="flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-bold flex-shrink-0"
        style={{
          background: state === 'done' ? 'rgba(31,90,71,0.10)' : SOVEREIGN.paper,
          color: state === 'done' ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption,
        }}
      >
        {step}
      </span>

      <div className="flex-1 min-w-0">
        <div
          className="text-[13px] font-semibold leading-tight"
          style={{ color: SOVEREIGN.ink }}
        >
          {title}
        </div>
        <div
          className="text-[11.5px] mt-0.5 leading-snug truncate"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          {summary}
        </div>
      </div>

      <span
        className="text-[10.5px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
        style={{ background: badge.bg, color: badge.text }}
      >
        {badge.label}
      </span>

      <ChevronRight
        size={16}
        className="transition-transform group-hover:translate-x-0.5 flex-shrink-0"
        style={{ color: SOVEREIGN.barTrack }}
      />
    </motion.button>
  );
}
